/**
 * @module composables/useSideloadUpload
 *
 * Owns an in-progress "Upload an episode file" upload — deliberately NOT
 * the SideloadView component.
 *
 * Why a singleton: the upload outlives the page that started it. Module-level
 * refs (same pattern as useAppMenu / useMediaPlayer) mean the state, the
 * running chunk loop, and the progress UI all survive in-app navigation —
 * so leaving the upload page doesn't interrupt anything and doesn't hide
 * what's happening. App.vue renders a persistent progress card from this,
 * visible on every page, with Resume / Cancel.
 *
 * What can still interrupt an upload, and what happens then:
 *   - In-app navigation: nothing. Same JS context, the loop keeps running.
 *   - Backgrounding the app / screen lock (iOS): the tab's JS and network are
 *     SUSPENDED by the OS — the in-flight chunk dies. Unavoidable; iOS has no
 *     background-upload API for web pages. api/sideload.js waits for the app
 *     to come back to the foreground, then resumes from the server's byte
 *     count, so this costs one chunk rather than the file.
 *   - A real page reload/close: tears down the JS context entirely, so the
 *     loop stops. The bytes already uploaded are still on the server and the
 *     episode row still exists — but the File handle is gone with the page,
 *     so resuming needs the user to pick the same file again. beforeUnload
 *     (registered here while an upload is active) warns before that happens.
 */
import { ref, shallowRef, computed } from "vue";
import { sideloadApi } from "@/api/sideload.js";
import { useAppMenu } from "./useAppMenu.js";
import { useTags } from "./useTags.js";

/** The File being uploaded. shallowRef: a plain ref() deep-proxies it, and a Proxy-wrapped File misbehaves in FormData/slice paths. */
const uploadFile = shallowRef(null);

/** Display name for the progress card. */
const fileName = ref("");

/** 0-100, driven by the server's acknowledged byte count (not local optimism). */
const percent = ref(0);

/**
 * idle | uploading | interrupted | processing
 * "interrupted" is a RESUMABLE state, not a failure: the session and its
 * bytes are intact server-side and pressing Resume continues from there.
 */
const status = ref("idle");

/** Server-side session + destination, retained across an interruption so Resume continues the SAME upload. */
const uploadId = ref(null);
const chunkSize = ref(0);
const podcastId = ref(null);
const episodeId = ref(null);

/** Why the upload stopped, shown on the progress card. */
const errorMessage = ref("");

/** Set by cancel() and polled between chunks so a cancel takes effect promptly. */
let cancelRequested = false;

/** Registered only while an upload is active (see the module comment). */
let beforeUnloadHandler = null;

/** Screen Wake Lock held during the upload; released when it ends. */
let wakeLock = null;

/** True while an upload is running or paused mid-way — drives the persistent card. */
const isActive = computed(() => status.value === "uploading" || status.value === "interrupted");

/**
 * Keep the screen awake and warn on a real page unload, for as long as an
 * upload is in flight. The Wake Lock only stops the SCREEN auto-locking (a
 * common way uploads die); it cannot stop deliberate app-switching — that's
 * an OS-level limit no web page can override.
 */
const attachUploadGuards = async () => {
  if (!beforeUnloadHandler) {
    beforeUnloadHandler = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnloadHandler);
  }
  if (!wakeLock && "wakeLock" in navigator) {
    // Best-effort: unsupported browsers and a rejected request (e.g. low
    // battery) must not block the upload.
    wakeLock = await navigator.wakeLock.request("screen").catch(() => null);
  }
};

/** Release both guards once no upload is in flight. */
const releaseUploadGuards = async () => {
  if (beforeUnloadHandler) {
    window.removeEventListener("beforeunload", beforeUnloadHandler);
    beforeUnloadHandler = null;
  }
  if (wakeLock) {
    await wakeLock.release().catch(() => {});
    wakeLock = null;
  }
};

/** Clear all per-upload state back to idle. */
const resetState = () => {
  uploadFile.value = null;
  fileName.value = "";
  percent.value = 0;
  status.value = "idle";
  uploadId.value = null;
  chunkSize.value = 0;
  podcastId.value = null;
  episodeId.value = null;
  errorMessage.value = "";
  cancelRequested = false;
};

/**
 * Run (or continue) the chunk loop for the current session, then complete.
 * Shared by startUpload and resumeUpload — the only difference between
 * starting and resuming is whether a session already exists.
 *
 * @returns {Promise<boolean>} true when the upload finished and was handed off
 */
const runChunkLoop = async () => {
  const { snackbar } = useAppMenu();
  status.value = "uploading";
  errorMessage.value = "";
  await attachUploadGuards();

  try {
    await sideloadApi.uploadFileInChunks({
      uploadId: uploadId.value,
      file: uploadFile.value,
      chunkSize: chunkSize.value,
      onProgress: (p) => {
        percent.value = p;
      },
      isCancelled: () => cancelRequested,
    });

    if (cancelRequested) return false;

    // All bytes are in — hand off to server-side processing, which continues
    // regardless of what this client does next (including being closed).
    status.value = "processing";
    await sideloadApi.completeSideload(uploadId.value);

    snackbar.value = {
      show: true,
      color: "success",
      message: `“${fileName.value}” uploaded — processing in the background`,
    };
    resetState();
    await releaseUploadGuards();
    return true;
  } catch (error) {
    // NOT a failure state: the session and its bytes survive server-side, so
    // this is a pause the user can resume from. The episode row also stays
    // (visible, spinning) rather than vanishing — the whole reason it's
    // created before any bytes are sent.
    status.value = "interrupted";
    errorMessage.value = error.message || "Upload interrupted";
    snackbar.value = {
      show: true,
      color: "warning",
      message: `Upload paused: ${errorMessage.value}`,
    };
    await releaseUploadGuards();
    return false;
  }
};

/**
 * Begin a new upload: creates the episode row + upload session FIRST (so the
 * episode is immediately visible with a spinner, and the app-bar activity
 * indicator counts it), then streams the file as resumable chunks.
 *
 * @param {Object} params
 * @param {File} params.file
 * @param {string} params.title
 * @param {string} [params.notes]
 * @param {number[]} [params.tagIds]
 * @param {string} [params.destinationPodcastId]
 * @param {string} [params.thumbnailUrl]
 * @param {File} [params.thumbnailFile]
 * @param {Object} [params.itunes] - Apple Podcasts / RSS item fields (all optional)
 * @param {string} [params.sourceUrl] - Optional page the file came from (shown as a link in the info dialog)
 * @returns {Promise<{podcastId: string}|null>} Destination on success, null if it paused
 */
const startUpload = async ({
  file,
  title,
  notes,
  tagIds,
  destinationPodcastId,
  thumbnailUrl,
  thumbnailFile,
  itunes,
  sourceUrl,
}) => {
  cancelRequested = false;
  uploadFile.value = file;
  fileName.value = file.name;
  percent.value = 0;
  errorMessage.value = "";
  status.value = "uploading";
  await attachUploadGuards();

  try {
    const init = await sideloadApi.initSideload({
      title,
      notes,
      tagIds,
      podcastId: destinationPodcastId || undefined,
      thumbnailUrl: thumbnailUrl || undefined,
      mimetype: file.type,
      totalBytes: file.size,
      itunes,
      sourceUrl: sourceUrl || undefined,
    });
    uploadId.value = init.uploadId;
    chunkSize.value = init.chunkSize;
    podcastId.value = init.podcastId;
    episodeId.value = init.episodeId;

    // The server just attached "sideloaded" (creating that tag on first use)
    // plus the picked tags to the new episode. The app's tag assignments are
    // loaded once per session and only updated locally by the tag UI, so
    // without a refetch the new episode showed no tag chips until a page
    // reload (reported 2026-09-15). Fire-and-forget: chips are cosmetic and
    // must never hold up the upload.
    if (!init.resumed) {
      useTags().loadTags({ force: true });
    }

    if (init.resumed) {
      // The server matched an unfinished session for this same file (the
      // usual cause: the page was reloaded mid-upload, which discards the
      // File handle but not the bytes already stored). Say so — the progress
      // bar starting at 40% instead of 0% would otherwise look like a bug,
      // and the user needs to know their earlier progress wasn't wasted.
      percent.value = Math.floor((init.receivedBytes / file.size) * 100);
      const { snackbar } = useAppMenu();
      snackbar.value = {
        show: true,
        color: "info",
        message: `Resuming “${file.name}” from ${percent.value}%`,
      };
    } else if (thumbnailFile) {
      // Small and one-shot — no chunking needed; phase 2 reads it from the
      // session. Skipped on a resume: the original session already has
      // whatever thumbnail was chosen the first time.
      await sideloadApi.uploadSideloadThumbnail(init.uploadId, thumbnailFile);
    }
  } catch (error) {
    // Failed before any session existed — nothing to resume, so this one IS
    // terminal. Surface it and go back to idle.
    const { snackbar } = useAppMenu();
    errorMessage.value = error.message || "Could not start the upload";
    snackbar.value = { show: true, color: "error", message: `Upload failed: ${errorMessage.value}` };
    resetState();
    await releaseUploadGuards();
    return null;
  }

  const destination = podcastId.value;
  const finished = await runChunkLoop();
  return finished ? { podcastId: destination } : null;
};

/**
 * Continue an interrupted upload from whatever the server already has.
 * Only possible while the File handle is still in memory — i.e. the page was
 * never fully reloaded (in-app navigation is fine).
 */
const resumeUpload = async () => {
  if (status.value !== "interrupted" || !uploadFile.value || !uploadId.value) return null;
  cancelRequested = false;
  const destination = podcastId.value;
  const finished = await runChunkLoop();
  return finished ? { podcastId: destination } : null;
};

/**
 * Abandon the upload: stops the loop, and asks the server to drop the
 * partial file and the placeholder episode so nothing is left spinning.
 */
const cancelUpload = async () => {
  cancelRequested = true;
  const id = uploadId.value;
  const name = fileName.value;
  resetState();
  await releaseUploadGuards();
  if (id) await sideloadApi.abortSideload(id);
  const { snackbar } = useAppMenu();
  snackbar.value = { show: true, color: "info", message: `Upload of “${name}” cancelled` };
};

/**
 * useSideloadUpload - shared upload state + actions.
 * @returns {Object}
 */
export function useSideloadUpload() {
  return {
    fileName,
    percent,
    status,
    errorMessage,
    episodeId,
    isActive,
    startUpload,
    resumeUpload,
    cancelUpload,
  };
}
